import type { components } from '@multaqa/api-client'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type SupplierBill = components['schemas']['BillResource']
export type SupplierPayment = components['schemas']['PaymentResource']

/** Every bill from this supplier, newest first as the API sorts them. */
export function useSupplierBills(companyId: string | null, supplierId: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'bills', { supplier: supplierId }],
    enabled: Boolean(companyId && supplierId),
    queryFn: async (): Promise<SupplierBill[]> => {
      if (!companyId) return []
      const rows: SupplierBill[] = []
      let page = 1
      let lastPage = 1
      while (page <= lastPage) {
        const response = await api.GET('/accounting/bills', {
          headers: { 'X-Company-Id': companyId },
          params: { query: { supplier: supplierId, page, per_page: 100 } },
        })
        if (!response.data) throw new Error('Supplier bills returned no body')
        rows.push(...response.data.data)
        lastPage = response.data.meta.last_page
        page += 1
      }
      return rows
    },
  })
}

/** Payments made to this supplier (outbound, partner = supplier). */
export function useSupplierPayments(companyId: string | null, supplierId: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'accounting', 'payments', 'supplier', supplierId],
    enabled: Boolean(companyId && supplierId),
    queryFn: async (): Promise<SupplierPayment[]> => {
      if (!companyId) return []
      const rows: SupplierPayment[] = []
      let page = 1
      let lastPage = 1
      while (page <= lastPage) {
        const response = await api.GET('/accounting/payments', {
          headers: { 'X-Company-Id': companyId },
          params: {
            query: { partner: supplierId, payment_direction: 'outbound', page, per_page: 100 },
          },
        })
        if (!response.data) throw new Error('Supplier payments returned no body')
        rows.push(...response.data.data)
        lastPage = response.data.meta.last_page
        page += 1
      }
      return rows
    },
  })
}
